import { useEffect, useRef, useState } from 'react';
import { getPreferences, PreferencesApiError, savePreferences } from '@peruselab/api-client/preferences';
import type { AnnotationStyle } from '@peruselab/structure-editor/domain/annotationStyle';

export type StyleDefaultsSyncStatus = 'loading' | 'saved' | 'saving' | 'unsaved';

const SAVE_DELAY_MS = 500;
const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * #862 (#842 S9, #773 C3) -- new-object style defaults follow the user across devices: loaded from
 * their account preferences when the workspace opens, saved back (debounced) whenever they change.
 * Never stored in drawings. When another device saved in the meantime (409), the latest document
 * is read and this change applied on top, once. When saving fails, the defaults still apply for
 * this session and the status says so.
 */
export function useStyleDefaultsSync(
  defaults: AnnotationStyle,
  setDefaults: (next: AnnotationStyle) => void
): StyleDefaultsSyncStatus {
  const [status, setStatus] = useState<StyleDefaultsSyncStatus>('loading');
  const stored = useRef<{ revision: number; preferences: Record<string, unknown> } | null>(null);
  const lastSaved = useRef<string | null>(null);
  // The defaults last sent; a failed save isn't retried until they change (a retry loop flipped the
  // status every half second and shook the Style tab).
  const lastAttempted = useRef<string | null>(null);
  const setDefaultsRef = useRef(setDefaults);
  useEffect(() => {
    setDefaultsRef.current = setDefaults;
  }, [setDefaults]);

  useEffect(() => {
    let current = true;
    getPreferences()
      .then((loaded) => {
        if (!current) return;
        stored.current = { revision: loaded.revision, preferences: { ...loaded.preferences } };
        const saved = loaded.preferences.styleDefaults;
        if (isObject(saved)) {
          lastSaved.current = JSON.stringify(saved);
          setDefaultsRef.current(saved as AnnotationStyle);
        }
        setStatus('saved');
      })
      .catch(() => {
        if (current) setStatus('unsaved');
      });
    return () => {
      current = false;
    };
  }, []);

  const serialized = JSON.stringify(defaults);
  useEffect(() => {
    // One save at a time: a change made while saving is picked up when the status settles.
    if (
      status === 'loading' ||
      status === 'saving' ||
      serialized === lastSaved.current ||
      serialized === lastAttempted.current
    ) {
      return;
    }
    const timer = setTimeout(() => {
      const save = async (retry: boolean): Promise<void> => {
        const base = stored.current ?? { revision: 0, preferences: {} };
        const next = { ...base.preferences, styleDefaults: JSON.parse(serialized) as unknown };
        try {
          const saved = await savePreferences(base.revision, next);
          stored.current = { revision: saved.revision, preferences: { ...saved.preferences } };
          lastSaved.current = serialized;
          setStatus('saved');
        } catch (error) {
          if (retry && error instanceof PreferencesApiError && error.status === 409) {
            const latest = await getPreferences();
            stored.current = { revision: latest.revision, preferences: { ...latest.preferences } };
            return save(false);
          }
          setStatus('unsaved');
        }
      };
      lastAttempted.current = serialized;
      setStatus('saving');
      void save(true).catch(() => setStatus('unsaved'));
    }, SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [serialized, status]);

  return status;
}
